import moment from 'moment';
import { useMemo } from 'react';
import computeAvailabilityColors from '../../utils/availabilityColors';

export const utcMoment = time => moment.tz(time, 'UTC');

export const ONE_DAY = 60 * 60 * 24; // seconds
export const MAX_DAYS = 14 * 7; // 14 weeks
export const LARGE_SCREEN_THRESHOLD = 21; // days

// A recurring schedule repeats every `scheduleLength` weeks, starting from
// `anchorDate` (which should always be a Monday at midnight). Intervals are
// stored as absolute timestamps within that range, e.g. an interval in week 2
// of a 2-week schedule has a start_time 7 days later than the equivalent
// interval in week 1.

// A stable id for an interval, used both as the React/FullCalendar key and to
// find back an interval after it has been changed.
export const intervalId = interval => `${interval.start_time}_${interval.end_time}`;

// Converts the local `intervals` representation into events that
// FullCalendar can render. See https://fullcalendar.io/docs/event-object
export const intervalsToEvents = intervals =>
  intervals
    .map(interval => {
      const colors = computeAvailabilityColors({
        available: interval.available,
        active: true,
        duty: interval.standby_duty
      });

      if (!colors) return null;

      return {
        id: intervalId(interval),
        start: interval.start_time,
        end: interval.end_time,
        backgroundColor: colors.color,
        borderColor: colors.borderColor,
        extendedProps: interval
      };
    })
    .filter(Boolean);

// Removes (or trims) any intervals that overlap with the given range. This
// is used before inserting/moving an interval, so the new interval always
// takes precedence over whatever was there before.
export const removeOverlap = (intervals, start, end) => {
  const rangeStart = moment(start);
  const rangeEnd = moment(end);
  const result = [];

  for (const interval of intervals) {
    const intervalStart = moment(interval.start_time);
    const intervalEnd = moment(interval.end_time);

    if (intervalEnd.isSameOrBefore(rangeStart) || intervalStart.isSameOrAfter(rangeEnd)) {
      // no overlap at all, keep as-is
      result.push(interval);
      continue;
    }

    // partial overlap: keep whatever part of the old interval falls outside
    // of the new range
    if (intervalStart.isBefore(rangeStart)) {
      result.push({ ...interval, start_time: interval.start_time, end_time: rangeStart.format() });
    }
    if (intervalEnd.isAfter(rangeEnd)) {
      result.push({ ...interval, start_time: rangeEnd.format(), end_time: interval.end_time });
    }
    // if fully contained within the new range, drop it entirely
  }

  return result;
};

// Removes every interval that falls (partially) beyond the given schedule
// length, counting from anchorDate. Used when the number of weeks is reduced.
export const clipIntervalsToScheduleLength = (intervals, anchorTime, scheduleLength) => {
  anchorTime = moment(anchorTime);
  const end = moment(anchorTime).add(scheduleLength, 'seconds');
  const result = removeOverlap(intervals, end.format(), moment(end).add(100, 'years').format());
  return removeOverlap(result, moment(anchorTime).subtract(100, 'years').format(), anchorTime);
};

export const useCurrentDay = (anchorDate, scheduleLength) => {
  return useMemo(() => {
    if (!anchorDate || !scheduleLength) return null;

    const current = moment.tz('UTC');
    const scheduleLengthInDays = scheduleLength / ONE_DAY;

    const schedulePeriodStart = utcMoment(anchorDate);
    const schedulePeriodEnd = utcMoment(anchorDate).add(scheduleLengthInDays, 'days');

    // we're going to add this time period to the current date until it falls
    // within the schedule range, so we need to know if the current date is
    // before or after the schedule range
    const delta = current.isBefore(schedulePeriodStart) ? scheduleLengthInDays : -scheduleLengthInDays;

    while (!current.isBetween(schedulePeriodStart, schedulePeriodEnd, null, '[]')) {
      current.add(delta, 'days');
    }

    return current;
  }, [anchorDate, scheduleLength]);
};
